import Dexie from 'dexie';

const db = new Dexie('ThreatScope');

db.version(1).stores({
  logs: '++id, source, status, timestamp, user, ip, country, importId, [source+status]',
  ipEnrichments: '&ip',
  threats: '++id, logId, ip, type, severity, status, classifiedAt, [status+severity]',
  imports: '++id, filename, source, status, importedAt',
});

db.version(2).stores({
  logs: '++id, source, status, timestamp, user, ip, country, importId, [source+status]',
  ipEnrichments: '&ip',
  threats: '++id, logId, ip, user, type, severity, status, classifiedAt, [status+severity]',
  imports: '++id, filename, source, status, importedAt',
}).upgrade(tx => {
  // Preserve all existing data — Dexie auto-populates new indexes
  // The only change is adding 'user' to threats indexes
});

db.version(3).stores({
  logs: '++id, source, status, timestamp, user, ip, country, importId, [source+status]',
  ipEnrichments: '&ip',
  threats: '++id, logId, ip, user, type, severity, status, classifiedAt, [status+severity]',
  imports: '++id, filename, source, status, importedAt',
  emailLogs: '++id, source, status, timestamp, sender, recipients, subject, senderIp, importId, messageId, [source+status]',
}).upgrade(tx => {
  // Preserve all existing data — Dexie auto-populates new indexes for v3
});

export async function addLogs(logs) {
  return db.logs.bulkAdd(logs);
}

export async function getAllLogs(filters = {}) {
  let collection = db.logs.toCollection();

  if (filters.source && filters.status) {
    collection = db.logs.where('[source+status]').equals([filters.source, filters.status]);
  } else if (filters.source) {
    collection = db.logs.where('source').equals(filters.source);
  } else if (filters.status) {
    collection = db.logs.where('status').equals(filters.status);
  }

  if (filters.importId) {
    if (filters.source || filters.status) {
      collection = collection.filter(l => l.importId === filters.importId);
    } else {
      collection = db.logs.where('importId').equals(filters.importId);
    }
  }

  return collection.toArray();
}

export async function getLog(id) {
  return db.logs.get(id);
}

export async function updateLog(id, changes) {
  return db.logs.update(id, changes);
}

export async function deleteLogs(ids) {
  return db.logs.bulkDelete(ids);
}

export async function deleteLogsByImport(importId) {
  const logs = await db.logs.where('importId').equals(importId).toArray();
  const ids = logs.map(l => l.id);
  if (ids.length) await db.logs.bulkDelete(ids);
  return ids;
}

export async function getDistinctIPs(filters = {}) {
  const logs = await getAllLogs(filters);
  return [...new Set(logs.map(l => l.ip).filter(Boolean))];
}

export async function countLogs() {
  return db.logs.count();
}

export async function addIpEnrichment(enrichment) {
  return db.ipEnrichments.put(enrichment);
}

export async function getIpEnrichment(ip) {
  return db.ipEnrichments.get(ip);
}

export async function bulkGetIpEnrichments(ips) {
  const results = await db.ipEnrichments.bulkGet(ips);
  const map = {};
  for (let i = 0; i < ips.length; i++) {
    if (results[i]) map[ips[i]] = results[i];
  }
  return map;
}

export async function getAllIpEnrichments() {
  return db.ipEnrichments.toArray();
}

export async function addThreat(threat) {
  return db.threats.add(threat);
}

export async function addThreats(threats) {
  return db.threats.bulkAdd(threats);
}

export async function getAllThreats(filters = {}) {
  let collection = db.threats.toCollection();

  if (filters.status && filters.severity) {
    collection = db.threats.where('[status+severity]').equals([filters.status, filters.severity]);
  } else if (filters.status) {
    collection = db.threats.where('status').equals(filters.status);
  } else if (filters.severity) {
    collection = db.threats.where('severity').equals(filters.severity);
  }

  if (filters.type) {
    if (filters.status || filters.severity) {
      collection = collection.filter(t => t.type === filters.type);
    } else {
      collection = db.threats.where('type').equals(filters.type);
    }
  }

  return collection.toArray();
}

export async function getThreat(id) {
  return db.threats.get(id);
}

export async function updateThreat(id, changes) {
  return db.threats.update(id, changes);
}

export async function bulkUpdateThreats(ids, changes) {
  return db.threats.bulkUpdate(ids.map(id => ({ key: id, changes })));
}

export async function countThreats(filters = {}) {
  if (filters.status && filters.severity) {
    return db.threats.where('[status+severity]').equals([filters.status, filters.severity]).count();
  }
  if (filters.status) {
    return db.threats.where('status').equals(filters.status).count();
  }
  if (filters.severity) {
    return db.threats.where('severity').equals(filters.severity).count();
  }
  if (filters.type) {
    return db.threats.where('type').equals(filters.type).count();
  }
  return db.threats.count();
}

export async function addImport(imp) {
  return db.imports.add(imp);
}

export async function getImport(id) {
  return db.imports.get(id);
}

export async function updateImport(id, changes) {
  return db.imports.update(id, changes);
}

export async function getAllImports() {
  return db.imports.orderBy('importedAt').reverse().toArray();
}

export async function deleteImport(id) {
  return db.imports.delete(id);
}

export async function deleteThreats(ids) {
  return db.threats.bulkDelete(ids);
}

export async function deleteThreatsByLogId(logIds) {
  const threats = await db.threats.where('logId').anyOf(logIds).toArray();
  const ids = threats.map(t => t.id);
  if (ids.length) await db.threats.bulkDelete(ids);
  return ids;
}

export async function getLoginStatsByUser(limit = 8) {
  const logs = await db.logs.toArray();
  const userMap = {};

  for (const log of logs) {
    if (!log.user) continue;
    if (!userMap[log.user]) {
      userMap[log.user] = { user: log.user, failed: 0, success: 0, total: 0 };
    }
    if (log.result === 'Success') {
      userMap[log.user].success++;
    } else {
      userMap[log.user].failed++;
    }
    userMap[log.user].total++;
  }

  return Object.values(userMap)
    .sort((a, b) => b.failed - a.failed)
    .slice(0, limit);
}

export async function getFailedLoginsByIp(limit = 50) {
  const logs = await db.logs.toArray();
  const ipMap = {};

  for (const log of logs) {
    if (!log.ip) continue;
    if (log.result === 'Success') continue;
    if (!ipMap[log.ip]) {
      ipMap[log.ip] = { ip: log.ip, failed: 0 };
    }
    ipMap[log.ip].failed++;
  }

  return Object.values(ipMap)
    .sort((a, b) => b.failed - a.failed)
    .slice(0, limit);
}

export async function getFailedLoginsByAsn(limit = 50) {
  const logs = await db.logs.toArray();

  // Count failures per IP
  const ipFailCount = {};
  for (const log of logs) {
    if (!log.ip || log.result === 'Success') continue;
    ipFailCount[log.ip] = (ipFailCount[log.ip] || 0) + 1;
  }

  const uniqueIps = Object.keys(ipFailCount);
  if (uniqueIps.length === 0) return [];

  // Bulk lookup enrichments for failed IPs
  const enrichments = await bulkGetIpEnrichments(uniqueIps);

  // Aggregate failures by ASN
  const asnMap = {};
  for (const ip of uniqueIps) {
    const count = ipFailCount[ip];
    const enrichment = enrichments[ip];
    const asn = enrichment?.asn || 'Unknown';
    const asnName = enrichment?.asnName || 'Unknown';
    const key = asn;

    if (!asnMap[key]) {
      asnMap[key] = { asn, asnName, failed: 0 };
    }
    asnMap[key].failed += count;
  }

  return Object.values(asnMap)
    .sort((a, b) => b.failed - a.failed)
    .slice(0, limit);
}

export async function addEmailLogs(logs) {
  return db.emailLogs.bulkAdd(logs);
}

export async function getAllEmailLogs(filters = {}) {
  let collection = db.emailLogs.toCollection();

  if (filters.source && filters.status) {
    collection = db.emailLogs.where('[source+status]').equals([filters.source, filters.status]);
  } else if (filters.source) {
    collection = db.emailLogs.where('source').equals(filters.source);
  } else if (filters.status) {
    collection = db.emailLogs.where('status').equals(filters.status);
  }

  if (filters.importId) {
    if (filters.source || filters.status) {
      collection = collection.filter(l => l.importId === filters.importId);
    } else {
      collection = db.emailLogs.where('importId').equals(filters.importId);
    }
  }

  return collection.toArray();
}

export async function getEmailLog(id) {
  return db.emailLogs.get(id);
}

export async function getEmailDistinctIPs(filters = {}) {
  const logs = await getAllEmailLogs(filters);
  return [...new Set(logs.map(l => l.senderIp).filter(Boolean))];
}

export async function countEmailLogs() {
  return db.emailLogs.count();
}

export async function deleteEmailLogs(ids) {
  return db.emailLogs.bulkDelete(ids);
}

export async function deleteEmailLogsByImport(importId) {
  const logs = await db.emailLogs.where('importId').equals(importId).toArray();
  const ids = logs.map(l => l.id);
  if (ids.length) await db.emailLogs.bulkDelete(ids);
  return ids;
}

export async function getEmailLogsByIP(ip) {
  return db.emailLogs.where('senderIp').equals(ip).toArray();
}

export async function updateEmailLog(id, changes) {
  return db.emailLogs.update(id, changes);
}

export async function clearAll() {
  await db.logs.clear();
  await db.ipEnrichments.clear();
  await db.threats.clear();
  await db.imports.clear();
  await db.emailLogs.clear();
}

export { db };